import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import useSEO from "@/hooks/useSEO";
import { PersonSchema, BreadcrumbSchema } from "@/components/SEOStructuredData";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { useAuth } from "@/hooks/useAuth";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SkipToContent from "@/components/SkipToContent";
import { TalentMediaGallery } from "@/components/TalentMediaGallery";
import ScrollReveal from "@/components/ScrollReveal";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TalentEditDialog } from "@/components/admin/TalentEditDialog";
import { ArrowLeft, Pencil } from "lucide-react";

// Import placeholder image
import placeholderTalent from "@/assets/placeholder-talent.jpg";

const TalentProfile = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const { localizedPath } = useLocalizedPath();
  const { hasAdminAccess } = useAuth();
  const queryClient = useQueryClient();
  const currentLang = i18n.language?.substring(0, 2) || "en";
  const [isEditOpen, setIsEditOpen] = useState(false);

  const { data: talent, isLoading } = useQuery({
    queryKey: ["public-talent", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("talent")
        .select("*")
        .eq("slug", id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // Get localized content
  const getLocalizedBio = () => {
    if (currentLang === "fr" && talent?.bio_fr) {
      return talent.bio_fr;
    }
    return talent?.bio || "";
  };

  const getLocalizedSpecialty = () => {
    if (currentLang === "fr" && talent?.specialty_fr) {
      return talent.specialty_fr;
    }
    return talent?.specialty || "";
  };

  // SEO for talent profile
  useSEO({
    title: talent?.name || "Talent Profile",
    description: getLocalizedBio().substring(0, 155) || `Professional voiceover artist and talent at First Stage Talent.`,
    image: talent?.image_url || undefined,
    type: "profile",
  });

  const breadcrumbs = [
    { name: currentLang === "fr" ? "Accueil" : "Home", url: `https://firststagetalent.com/${currentLang}` },
    { name: t("talentPage.title"), url: `https://firststagetalent.com/${currentLang}/talent` },
    ...(talent ? [{ name: talent.name, url: `https://firststagetalent.com/${currentLang}/talent/${id}` }] : []),
  ];

  const getImageUrl = () => {
    if (talent?.image_url && talent.image_url.startsWith("http")) {
      return talent.image_url;
    }
    return placeholderTalent;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <SkipToContent />
        <Header />
        <main id="main-content" tabIndex={-1} className="outline-none">
          <div className="pt-28 md:pt-32 pb-4">
            <div className="container-wide">
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <section className="pb-20 md:pb-28">
            <div className="container-wide">
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
                <Skeleton className="aspect-[4/5] w-full" />
                <div className="space-y-8">
                  <Skeleton className="h-12 w-3/4" />
                  <Skeleton className="h-6 w-1/2" />
                  <Skeleton className="h-32 w-full" />
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  if (!talent) {
    return (
      <div className="min-h-screen">
        <SkipToContent />
        <Header />
        <main id="main-content" tabIndex={-1} className="outline-none section-padding">
          <div className="container-wide text-center">
            <h1 className="heading-section mb-4">{t("talentProfile.notFound")}</h1>
            <p className="text-muted-foreground mb-8">
              {t("talentProfile.notFoundDesc")}
            </p>
            <Button asChild variant="sectionCta">
              <Link to={localizedPath("/talent")}>{t("talentProfile.browseAll")}</Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SkipToContent />
      <Header />
      <main id="main-content" tabIndex={-1} className="outline-none">
        {/* Back Link */}
        <div className="pt-28 md:pt-32 pb-4">
          <div className="container-wide">
            <Link 
              to={localizedPath("/talent")} 
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft size={16} />
              {t("talentProfile.backToTalent")}
            </Link>
          </div>
        </div>

        {/* Profile Content */}
        <section className="pb-20 md:pb-28">
          <div className="container-wide">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Image */}
              <ScrollReveal animation="slideLeft">
                <div className="aspect-[4/5] bg-muted overflow-hidden">
                  <img
                    src={getImageUrl()}
                    alt={talent.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </ScrollReveal>

              {/* Info */}
              <ScrollReveal animation="slideRight" className="space-y-8">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h1 className="heading-display mb-2 text-primary">{talent.name}</h1>
                    <p className="text-lg text-muted-foreground">{getLocalizedSpecialty()}</p>
                  </div>
                  {hasAdminAccess && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setIsEditOpen(true)}
                      className="shrink-0"
                      aria-label="Edit talent"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {talent.languages?.map((lang: string) => (
                    <span
                      key={lang}
                      className="text-xs px-3 py-1.5 bg-primary text-primary-foreground"
                    >
                      {lang}
                    </span>
                  ))}
                </div>

                {/* Bio */}
                {getLocalizedBio() && (
                  <div>
                    <h2 className="text-sm uppercase tracking-wide font-medium mb-3">{t("talentProfile.about")}</h2>
                    <p className="text-editorial">{getLocalizedBio()}</p>
                  </div>
                )}

                {/* Media Portfolio */}
                <TalentMediaGallery talentId={talent.id} talentName={talent.name} />

                {/* CTA */}
                <div className="pt-4">
                  <Button asChild size="lg" className="w-full sm:w-auto">
                    <Link 
                      to={localizedPath("/contact")} 
                      state={{ talentName: talent.name, talentId: talent.id, talentSlug: talent.slug }}
                    >
                      {t("talentProfile.requestTalent")}
                    </Link>
                  </Button>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* Edit Dialog */}
        {hasAdminAccess && talent && (
          <TalentEditDialog
            open={isEditOpen}
            onOpenChange={setIsEditOpen}
            talent={{
              id: talent.id,
              name: talent.name,
              slug: talent.slug,
              specialty: talent.specialty,
              languages: talent.languages || [],
              image_url: talent.image_url || "",
              bio: talent.bio || "",
              is_active: talent.is_active,
              is_featured: talent.is_featured || false,
              display_order: talent.display_order || 0,
            }}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ["public-talent", id] });
            }}
          />
        )}
      </main>
      <Footer />
    </div>
  );
};

export default TalentProfile;
